import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DollarSign, Upload, Download, CheckCircle, XCircle, FileText, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const ElectronicRemittancePage = () => {
  const [remittances, setRemittances] = useState([
    { id: '1', payer: 'Medicare', checkNumber: 'CHK-001234', checkDate: '01/25/2026', amount: '$12,450.00', claims: 15, status: 'Pending', posted: false },
    { id: '2', payer: 'Medicaid', checkNumber: 'CHK-005678', checkDate: '01/24/2026', amount: '$8,320.50', claims: 12, status: 'Posted', posted: true },
    { id: '3', payer: 'UnitedHealthcare', checkNumber: 'CHK-009012', checkDate: '01/23/2026', amount: '$5,780.25', claims: 8, status: 'Pending', posted: false },
    { id: '4', payer: 'Aetna', checkNumber: 'CHK-003456', checkDate: '01/22/2026', amount: '$3,240.00', claims: 5, status: 'Posted', posted: true },
    { id: '5', payer: 'BCBS', checkNumber: 'CHK-007890', checkDate: '01/21/2026', amount: '$9,150.75', claims: 11, status: 'Partial', posted: false },
  ]);

  const [selectedRemittances, setSelectedRemittances] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRemittances(remittances.filter(r => !r.posted).map(r => r.id));
    } else {
      setSelectedRemittances([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedRemittances(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handlePost = () => {
    if (selectedRemittances.length === 0) {
      toast.error('Please select remittances to post');
      return;
    }
    toast.success(`Posting ${selectedRemittances.length} remittance(s)...`);
    setRemittances(prev => prev.map(r => 
      selectedRemittances.includes(r.id) ? { ...r, status: 'Posted', posted: true } : r
    ));
    setSelectedRemittances([]);
  };

  const handleImport = () => {
    toast.info('Import ERA/835 files feature');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Posted':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Posted</span>;
      case 'Pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">Pending</span>;
      case 'Partial':
        return <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">Partial</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const filteredRemittances = remittances.filter(r => 
    r.payer.toLowerCase().includes(searchQuery.toLowerCase()) || 
    r.checkNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPending = remittances.filter(r => !r.posted).reduce((sum, r) => sum + parseFloat(r.amount.replace(/[$,]/g, '')), 0);
  const totalPosted = remittances.filter(r => r.posted).reduce((sum, r) => sum + parseFloat(r.amount.replace(/[$,]/g, '')), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="electronic-remittance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#27ae60] rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Electronic Remittance Posting</h1>
              <p className="text-gray-600">Import and post ERA/835 remittance files</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleImport}>
              <Upload className="w-4 h-4 mr-2" /> Import ERA
            </Button>
            <Button className="bg-[#27ae60] hover:bg-[#219a52]" onClick={handlePost}>
              <CheckCircle className="w-4 h-4 mr-2" /> Post Selected
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Total Remittances</p>
            <p className="text-2xl font-bold text-gray-900">{remittances.length}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Pending Amount</p>
            <p className="text-2xl font-bold text-yellow-600">${totalPending.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Posted Amount</p>
            <p className="text-2xl font-bold text-green-600">${totalPosted.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Total Claims</p>
            <p className="text-2xl font-bold text-blue-600">{remittances.reduce((sum, r) => sum + r.claims, 0)}</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by payer or check number..." className="pl-10" />
            </div>
            <Button variant="outline" size="sm"><Filter className="w-4 h-4 mr-2" /> Filter</Button>
            <Button variant="outline" size="sm"><Download className="w-4 h-4 mr-2" /> Export</Button>
          </div>
        </div>

        {/* Remittances Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input type="checkbox" onChange={handleSelectAll} className="rounded" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Payer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Check #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Check Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Claims</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRemittances.map((remittance) => (
                  <tr key={remittance.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input 
                        type="checkbox" 
                        checked={selectedRemittances.includes(remittance.id)} 
                        onChange={() => handleSelect(remittance.id)} 
                        disabled={remittance.posted}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{remittance.payer}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono">{remittance.checkNumber}</td>
                    <td className="px-4 py-3 text-gray-600">{remittance.checkDate}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{remittance.amount}</td>
                    <td className="px-4 py-3 text-center">{remittance.claims}</td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(remittance.status)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => toast.info(`Viewing ${remittance.checkNumber}`)}>
                        <FileText className="w-4 h-4 mr-1" /> Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ElectronicRemittancePage;
